// AM Advertising Home — handoff NOTES on an alert. PURE + tested.
//
// ⚠️⚠️ NOTES ARE SEPARATE FROM ACKS, AND THAT IS THE LOAD-BEARING DECISION.
// Acks are PER-SURFACE and EXPIRE AT 72h. Notes are SHARED across surfaces and
// PERSIST. One sentence, and it is the whole model. Three reasons a note must
// not be a field on the ack doc, the first decisive:
//
//  1. THE ACK EXPIRES; THE NOTE MUST NOT. A note living on the ack would
//     disappear at exactly the moment the alert re-fires and the next person
//     needs the context. Backwards.
//  2. The flow is a CONVERSATION — specialist writes, AM reads, AM responds.
//     One note per ack is not a thread.
//  3. A specialist will want to leave a note WITHOUT clearing ("waiting on the
//     client, don't clear").
//
// So a note is filed against the alert's THREE-PART identity
// (`alertIdentityKey` — account + channel + type, no surface), which is exactly
// what lets the AM read what the Meta team wrote.
//
// APPEND-ONLY: no edit, no delete. A handoff log that can be rewritten is worth
// nothing. Rendered as TEXT, never HTML. Audited on create.

import type { AlertNote } from '../../types/advertisingHome.js';

/**
 * Body cap. Generous enough for real context, small enough that a note is a
 * note rather than a document pasted into the triage board.
 */
export const NOTE_BODY_MAX = 1000;

/**
 * ⚠️ THE DEFAULT DISPLAY WINDOW — a MITIGATION, NOT A FIX, and it is worth
 * being honest about which.
 *
 * An account that no-spends most Mondays accumulates forty notes against the
 * same identity in a year, and an AM reading a March note about a completely
 * different incident is being ACTIVELY MISLED — worse than seeing no note at
 * all. The right fix is an alert EPISODE id (this firing, not this identity),
 * scoping notes to the occurrence they were written about. No such id exists:
 * `evaluateAlerts` is stateless and re-derives every alert from scratch on each
 * pull, so there is nothing that persists across firings to key on.
 *
 * Until there is, recency is the proxy: the last 14 days show by default and
 * everything older stays REACHABLE behind a toggle — a handoff log you cannot
 * read all of is not a log. Do not mistake this for solving the problem.
 *
 * ⚠️ "REACHABLE" MEANS WITHIN THE SERVER'S READ HORIZON, and that is a real
 * limit rather than a rounding error: `listAlertNotes` bounds its read at
 * NOTE_LIST_MAX_AGE_DAYS, so a note older than that is not behind the toggle —
 * it is not on the page at all. This function never drops anything it is
 * given; the horizon upstream does. Said plainly because a UI that promised
 * "all earlier notes" would be lying, and nobody would find out.
 */
export const NOTE_RECENT_DAYS = 14;
const NOTE_RECENT_MS = NOTE_RECENT_DAYS * 24 * 60 * 60 * 1000;

/**
 * The toggle's label. ⚠️ THE CAVEAT IS IN THE LABEL, not only in this comment:
 * without the second clause a reader opens "earlier notes" and takes a
 * three-week-old note as context on today's firing, which is precisely the
 * misreading the missing episode id makes possible.
 */
export const OLDER_NOTES_TOGGLE_LABEL =
  'Show earlier notes — may relate to a different incident';

/** Notes newest-first — the order a handoff conversation is read in. */
export function sortNotesNewestFirst(notes: readonly AlertNote[]): AlertNote[] {
  return [...notes].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

export interface NoteRecencySplit {
  /** Shown by default. */
  recent: AlertNote[];
  /** Reachable behind the "older notes" toggle — never discarded. */
  older: AlertNote[];
}

/**
 * Split one alert's notes into the recent window and everything before it.
 *
 * ⚠️ An UNPARSEABLE `createdAt` counts as RECENT — this FAILS OPEN. The
 * alternative hides a note behind a toggle nobody thinks to open on the
 * strength of a timestamp we could not read, and the two mistakes do not cost
 * the same: a stale note SHOWN is visible and can be judged by the person
 * reading it, a fresh note HIDDEN is not.
 *
 * ⚠️⚠️ THIS IS THE OPPOSITE DEFAULT FROM `contactGrantAccountIds` AND
 * `isAckActive`, WHICH BOTH FAIL CLOSED, and both are right — the rule is not
 * "always fail closed", it is WHAT THE VALUE GOVERNS. Those two govern ACCESS
 * and SUPPRESSION: an unreadable value there could show a client another
 * client's numbers, or hide a live alert, and no human is in the loop to catch
 * it. This governs HUMAN JUDGEMENT on a page a colleague is already reading,
 * where the recovery is them looking at the date. Failing closed here would
 * withhold information from the only party able to evaluate it. Recorded
 * because the two sit in one codebase and read as inconsistent otherwise.
 */
export function splitNotesByRecency(
  notes: readonly AlertNote[],
  nowMs: number,
): NoteRecencySplit {
  const recent: AlertNote[] = [];
  const older: AlertNote[] = [];
  for (const note of sortNotesNewestFirst(notes)) {
    const at = Date.parse(note.createdAt);
    if (!Number.isFinite(at) || nowMs - at <= NOTE_RECENT_MS) recent.push(note);
    else older.push(note);
  }
  return { recent, older };
}

/**
 * Every note on the board, grouped by the alert identity it was filed against.
 *
 * ⚠️⚠️ THE GROUPING IS IN MEMORY, AND THAT IS THE READ SHAPE — stated here so
 * the next change knows what it is changing. `listAlertNotes` is ONE UNSCOPED
 * RANGE read on `createdAt` over the whole collection (bounded by the server's
 * NOTE_LIST_MAX_AGE_DAYS horizon); this splits it by alert. It is NOT an
 * equality read per alert, for two reasons: a panel showing N alerts would
 * otherwise cost N reads on every page load, and an equality on `alertKey`
 * PLUS `orderBy('createdAt')` needs a COMPOSITE INDEX where a bare range needs
 * none. Same posture as `listAcknowledgements`, which is also unscoped.
 *
 * ⚠️ SO `NOTE_RECENT_DAYS` IS ABOUT COMPREHENSION, NOT PERFORMANCE. The read is
 * bounded by the server horizon, not by the display window — do not start
 * treating that constant as the thing keeping this cheap, because shrinking it
 * would hide notes from readers and save nothing at all.
 *
 * REVISIT TRIGGER: notes going HIGH-VOLUME — anything auto-generated, or a few
 * thousand docs inside the horizon. Then the shape becomes a per-alert equality
 * read plus its composite index and this function goes. Hand-written handoff
 * notes across the roster are nowhere near it.
 */
export function groupNotesByAlert(notes: readonly AlertNote[]): Map<string, AlertNote[]> {
  const map = new Map<string, AlertNote[]>();
  for (const note of notes) {
    const list = map.get(note.alertKey);
    if (list) list.push(note);
    else map.set(note.alertKey, [note]);
  }
  return map;
}

/**
 * Is this body storable? Trimmed, non-empty, within the cap.
 *
 * ⚠️ The SAME rule the router's zod schema enforces, so a body the compose box
 * accepts cannot be one the API rejects. Returns the reason rather than a
 * boolean, because the box has to say why the button is disabled.
 */
export function noteBodyError(body: string): string | null {
  const trimmed = body.trim();
  if (!trimmed) return 'A note needs something in it.';
  if (trimmed.length > NOTE_BODY_MAX) {
    return `A note is at most ${NOTE_BODY_MAX} characters (this one is ${trimmed.length}).`;
  }
  return null;
}
